/**
 * EF-PWA-BRANDING-V1-STEP2: Tenant Context Detection
 * 
 * Determines whether the current context is:
 * - Platform context (eventsflo.ai, preview.emergentagent.com base domain)
 * - Tenant context (tenant subdomain or tenant slug in URL)
 * 
 * Rules:
 * - Platform login (/login without tenant) → EventsFlo branding
 * - Tenant subdomain (icrew.eventsflo.ai) → Tenant branding
 * - Client portal (/client/*) → Always tenant branding
 * - Admin with tenant → Tenant branding
 */

// Known platform domains (no tenant branding)
const PLATFORM_DOMAINS = [
  'eventsflo.ai',
  'www.eventsflo.ai',
  'app.eventsflo.ai',
  'localhost',
];

// Preview/development domains that are tenant-specific
const PREVIEW_DOMAIN_PATTERN = /\.preview\.emergentagent\.com$/;

/**
 * Detect if current context is a tenant-specific context
 * @returns {Object} { isTenantContext: boolean, tenantSlug: string|null }
 */
export function detectTenantContext() {
  const hostname = window.location.hostname;
  const pathname = window.location.pathname;
  
  // Client portal is ALWAYS tenant context
  if (pathname.startsWith('/client')) {
    return {
      isTenantContext: true,
      tenantSlug: 'client-portal',
      source: 'client-portal-route'
    };
  }
  
  // Check if we're on a preview/development domain
  // These are considered tenant-specific as they host a single tenant
  if (PREVIEW_DOMAIN_PATTERN.test(hostname)) {
    return {
      isTenantContext: true,
      tenantSlug: hostname.split('.')[0], // e.g., 'crew-onboard'
      source: 'preview-domain'
    };
  }
  
  // Check for subdomain-based tenant
  // e.g., icrew.eventsflo.ai → tenant = 'icrew'
  const parts = hostname.split('.');
  if (parts.length >= 3) {
    const subdomain = parts[0];
    const baseDomain = parts.slice(-2).join('.');
    
    // If subdomain is not 'www' or 'app' and base domain is eventsflo.ai
    if (!['www', 'app'].includes(subdomain) && baseDomain === 'eventsflo.ai') {
      return {
        isTenantContext: true,
        tenantSlug: subdomain,
        source: 'subdomain'
      };
    }
  }
  
  // Check for tenant slug in URL path
  // e.g., /icrew/login → tenant = 'icrew'
  const pathParts = pathname.split('/').filter(Boolean);
  if (pathParts.length > 0) {
    const possibleTenantSlug = pathParts[0];
    // Exclude known platform routes
    const platformRoutes = ['login', 'logout', 'settings', 'dashboard', 'staff', 
                           'projects', 'shifts', 'clients', 'venues', 'inbox',
                           'crew', 'reports', 'admin'];
    
    if (!platformRoutes.includes(possibleTenantSlug)) {
      return {
        isTenantContext: true,
        tenantSlug: possibleTenantSlug,
        source: 'url-path'
      };
    }
  }
  
  // Check if hostname is a known platform domain
  if (PLATFORM_DOMAINS.includes(hostname)) {
    return {
      isTenantContext: false,
      tenantSlug: null,
      source: 'platform-domain'
    };
  }
  
  // Default: For unknown domains, assume tenant context
  // This handles custom domains pointing to the platform
  return {
    isTenantContext: true,
    tenantSlug: hostname,
    source: 'custom-domain'
  };
}

/**
 * Default platform branding URL (used when NOT in tenant context)
 * EF-BRANDING-SYSTEM-V2: Updated to use new transparent logo
 */
const DEFAULT_PLATFORM_LOGO_URL = "https://customer-assets.emergentagent.com/job_45a8a4fc-b383-40c2-9d25-cacc8f29ed2a/artifacts/1xs77usv_eventsflo_logo_transparent.png";

/**
 * Default EventsFlo branding (used when NOT in tenant context)
 * EF-BRANDING-SYSTEM-V2: Updated with default platform logo URL
 */
export const EVENTSFLO_BRANDING = {
  company_name: 'EventsFlo',
  logo_url: DEFAULT_PLATFORM_LOGO_URL,
  has_custom_branding: false,
  is_platform_branding: true
};

/**
 * Fetch platform branding from backend
 * EF-BRANDING-SYSTEM-V2: New function to get platform branding from settings
 * EF-SETTINGS-CONSOLIDATION-V1 (PHASE 5): Uses unified /api/public/branding endpoint
 * @returns {Promise<Object>}
 */
export async function fetchPlatformBranding() {
  try {
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    // EF-SETTINGS-CONSOLIDATION-V1 (PHASE 5): Use unified branding endpoint
    const response = await fetch(`${API_URL}/api/public/branding`);
    if (!response.ok) throw new Error('Failed to fetch branding');
    const data = await response.json();
    
    // For platform context, use platform_* fields from the unified response
    return {
      company_name: data.platform_name || 'EventsFlo',
      logo_url: data.platform_logo_url || DEFAULT_PLATFORM_LOGO_URL,
      logo_dark_url: data.platform_logo_dark_url,
      favicon_url: data.platform_favicon_url,
      has_custom_branding: false,
      is_platform_branding: true
    };
  } catch (error) {
    console.warn('Failed to fetch platform branding, using defaults:', error);
    return EVENTSFLO_BRANDING;
  }
}

/**
 * Check if we should use EventsFlo platform branding
 * @returns {boolean}
 */
export function shouldUsePlatformBranding() {
  const context = detectTenantContext();
  return !context.isTenantContext;
}

/**
 * Get the current tenant context info
 * @returns {Object}
 */
export function getTenantContext() {
  return detectTenantContext();
}
